import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { format } from "date-fns";
import { PlayCircle, CheckCircle, XCircle, Clock, Loader2, Terminal } from "lucide-react";
import { runNetworkTest, getDevices } from "../api/axios";

export default function TestUtilities() {
  const [selectedTestType, setSelectedTestType] = useState<string>("ping");
  const [selectedDevice, setSelectedDevice] = useState<string>("");
  const [testParams, setTestParams] = useState({
    // Existing test params
    pingCount: "4",
    pingTimeout: "2",
    tracerouteMaxHops: "30",
    dnsServer: "8.8.8.8",
    dnsRecord: "A",
    dnsName: "example.com",
    httpUrl: "https://example.com",
    httpMethod: "GET",
    portHost: "",
    portNumber: "80",
    // SNMP params
    snmpVersion: "2c",
    
   
  });
  
  // Get devices
  const { data: devices = [], isLoading: isLoadingDevices, error: devicesError } = useQuery({
    queryKey: ["devices"],
    queryFn: getDevices,
  });

  // Store test results
  const [testResults, setTestResults] = useState<any[]>([]);

  // Run test mutation
  const runTestMutation = useMutation({
    mutationFn: async (testData: any) => {
      const deviceId = testData.deviceId;
      const params: any = { type: testData.testType };

            switch (testData.testType) {
        case "ping":
          params.type = "ping";
          break;

        case "traceroute":
          params.type = "traceroute";
          break;

        case "dns":
          params.type = "dns_lookup";
          params.target = testParams.dnsName;
          break;

        case "http":
          params.type = "http_check";
          params.url = testParams.httpUrl;
          params.method = testParams.httpMethod;
          break;

        case "port":
          params.type = "port_check";
          params.port = parseInt(testParams.portNumber);
          params.protocol = "tcp";
          break;

        case "snmp":
          params.type = "snmp_test";   // ✅ match backend switch-case
          break;

        case "ssh":
          params.type = "ssh_test";    // ✅ match backend switch-case
          break;

        default:
          throw new Error("Invalid test type");
      }


      return runNetworkTest(deviceId, params);
    },
    onSuccess: (data) => {
      const newResult = {
        id: Date.now(),
        deviceId: selectedDevice,
        testType: selectedTestType,
        status: data.success ? "success" : "failed",
        output: JSON.stringify(data, null, 2),
        timestamp: new Date().toISOString(),
        command: `${selectedTestType} test`,
        metrics: data
      };
      setTestResults(prev => [newResult, ...prev]);
    },
  });

  const handleParamChange = (param: string, value: string) => {
    setTestParams(prev => ({
      ...prev,
      [param]: value,
    }));
  };

  const handleRunTest = () => {
    if (!selectedDevice) return;
    runTestMutation.mutate({
      deviceId: selectedDevice,
      testType: selectedTestType,
    });
  };

  const getDeviceName = (deviceId: string) => {
    const device = devices.find((d: any) => d._id === deviceId || d.id === deviceId);
    return device ? device.name : `Device ${deviceId}`;
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "PPp");
    } catch (e) {
      return "Invalid date";
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "success":
        return <Badge className="bg-green-500"><CheckCircle className="mr-1 h-3 w-3" /> Success</Badge>;
      case "failed":
        return <Badge variant="destructive"><XCircle className="mr-1 h-3 w-3" /> Failed</Badge>;
      case "running":
        return <Badge className="bg-blue-500"><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Running</Badge>;
      default:
        return <Badge variant="outline"><Clock className="mr-1 h-3 w-3" /> {status}</Badge>;
    }
  };

  return (
    <div className="container mx-auto py-6">
      <h1 className="text-3xl font-bold mb-6">Network Test Utilities</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Test Configuration</CardTitle>
              <CardDescription>
                Configure and run network diagnostics
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Device selection */}
              <div className="space-y-2">
                <Label htmlFor="device-select">Target Device</Label>
                <Select value={selectedDevice} onValueChange={setSelectedDevice}>
                  <SelectTrigger id="device-select">
                    <SelectValue placeholder="Select a device" />
                  </SelectTrigger>
                  <SelectContent>
                    {devicesError ? (
                      <SelectItem value="error" disabled>Failed to load devices</SelectItem>
                    ) : isLoadingDevices ? (
                      <SelectItem value="loading" disabled>Loading devices...</SelectItem>
                    ) : devices.length === 0 ? (
                      <SelectItem value="none" disabled>No devices available</SelectItem>
                    ) : (
                      devices.map((device: any) => (
                        device && (device._id || device.id) ? (
                          <SelectItem key={device._id || device.id} value={device._id || device.id}>
                            {device.name} ({device.ipAddress})
                          </SelectItem>
                        ) : null
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Test Type */}
              <div className="space-y-2">
                <Label htmlFor="test-type">Test Type</Label>
                <Select value={selectedTestType} onValueChange={setSelectedTestType}>
                  <SelectTrigger id="test-type">
                    <SelectValue placeholder="Select test type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ping">Ping Test</SelectItem>
                    <SelectItem value="traceroute">Traceroute</SelectItem>
                    <SelectItem value="dns">DNS Lookup</SelectItem>
                    <SelectItem value="http">HTTP Check</SelectItem>
                    <SelectItem value="port">Port Check</SelectItem>
                    <SelectItem value="snmp">SNMP Test</SelectItem>
                    <SelectItem value="ssh">SSH Test</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Ping Test Parameters */}
              {selectedTestType === "ping" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="ping-count">Packet Count</Label>
                    <Input id="ping-count" type="number" min="1" max="20"
                      value={testParams.pingCount}
                      onChange={(e) => handleParamChange("pingCount", e.target.value)} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ping-timeout">Timeout (seconds)</Label>
                    <Input id="ping-timeout" type="number" min="1" max="10"
                      value={testParams.pingTimeout}
                      onChange={(e) => handleParamChange("pingTimeout", e.target.value)} />
                  </div>
                </div>
              )}

              {/* Traceroute Parameters */}
              {selectedTestType === "traceroute" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="traceroute-max-hops">Max Hops</Label>
                    <Input id="traceroute-max-hops" type="number" min="1" max="64"
                      value={testParams.tracerouteMaxHops}
                      onChange={(e) => handleParamChange("tracerouteMaxHops", e.target.value)} />
                  </div>
                </div>
              )}

              {/* DNS Lookup Parameters */}
              {selectedTestType === "dns" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="dns-server">DNS Server</Label>
                    <Input id="dns-server"
                      value={testParams.dnsServer}
                      onChange={(e) => handleParamChange("dnsServer", e.target.value)} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dns-record">Record Type</Label>
                    <Select value={testParams.dnsRecord}
                      onValueChange={(value) => handleParamChange("dnsRecord", value)}>
                      <SelectTrigger id="dns-record">
                        <SelectValue placeholder="Record Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="AAAA">AAAA</SelectItem>
                        <SelectItem value="MX">MX</SelectItem>
                        <SelectItem value="NS">NS</SelectItem>
                        <SelectItem value="TXT">TXT</SelectItem>
                        <SelectItem value="CNAME">CNAME</SelectItem>
                        <SelectItem value="SOA">SOA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dns-name">Domain Name</Label>
                    <Input id="dns-name"
                      value={testParams.dnsName}
                      onChange={(e) => handleParamChange("dnsName", e.target.value)} />
                  </div>
                </div>
              )}

              {/* HTTP Check Parameters */}
              {selectedTestType === "http" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="http-url">URL</Label>
                    <Input id="http-url"
                      value={testParams.httpUrl}
                      onChange={(e) => handleParamChange("httpUrl", e.target.value)} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="http-method">HTTP Method</Label>
                    <Select value={testParams.httpMethod}
                      onValueChange={(value) => handleParamChange("httpMethod", value)}>
                      <SelectTrigger id="http-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GET">GET</SelectItem>
                        <SelectItem value="HEAD">HEAD</SelectItem>
                        <SelectItem value="POST">POST</SelectItem>
                        <SelectItem value="PUT">PUT</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Port Check Parameters */}
              {selectedTestType === "port" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="port-host">Host</Label>
                    <Input id="port-host"
                      value={testParams.portHost}
                      onChange={(e) => handleParamChange("portHost", e.target.value)} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="port-number">Port Number</Label>
                    <Input id="port-number" type="number" min="1" max="65535"
                      value={testParams.portNumber}
                      onChange={(e) => handleParamChange("portNumber", e.target.value)} />
                  </div>
                </div>
              )}

              {/* SNMP Test Parameters */}
              {selectedTestType === "snmp" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="snmp-version">SNMP Version</Label>
                    <Select value={testParams.snmpVersion}
                      onValueChange={(value) => handleParamChange("snmpVersion", value)}>
                      <SelectTrigger id="snmp-version">
                        <SelectValue placeholder="Select SNMP Version" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Version 1</SelectItem>
                        <SelectItem value="2c">Version 2c</SelectItem>
                        <SelectItem value="3">Version 3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                </div>
              )}

              
            </CardContent>

            <CardFooter>
              <Button
                className="w-full"
                onClick={handleRunTest}
                disabled={!selectedDevice || runTestMutation.isPending}
              >
                {runTestMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Running Test...
                  </>
                ) : (
                  <>
                    <PlayCircle className="mr-2 h-4 w-4" />
                    Run Test
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* Right Panel (Results) */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Test Results</CardTitle>
              <CardDescription>View and analyze network test results</CardDescription>
            </CardHeader>
            <CardContent>
              {testResults.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <Terminal className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                  <p>No test results yet. Run your first network test.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <AnimatePresence mode="popLayout">
                    {testResults.map((result) => (
                      <motion.div
                        key={result.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ 
                          duration: 0.2,
                          ease: [0.4, 0, 0.2, 1]
                        }}
                      >
                        <Card className="overflow-hidden">
                          <CardHeader className="py-3">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center">
                                  <h3 className="text-lg font-medium capitalize">
                                    {result.testType} Test
                                  </h3>
                                  <span className="mx-2 text-muted-foreground">•</span>
                                  <span className="text-sm text-muted-foreground">
                                    {getDeviceName(result.deviceId)}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {formatDate(result.timestamp)}
                                </p>
                              </div>
                              <div>{getStatusBadge(result.status)}</div>
                            </div>
                          </CardHeader>
                          <Separator />
                          <CardContent className="py-3">
                            <div className="space-y-2">
                              <div className="flex items-center text-sm">
                                <span className="font-medium">Command:</span>
                                <code className="ml-2 px-1 py-0.5 bg-muted rounded text-xs">
                                  {result.command}
                                </code>
                              </div>
                              <div className="bg-muted rounded p-2 my-2 max-h-60 overflow-auto font-mono text-xs whitespace-pre w-full">
                                {result.output || "No output available"}
                              </div>
                              {result.metrics && Object.keys(result.metrics).length > 0 && (
                                <div className="mt-3">
                                  <h4 className="text-sm font-medium mb-1">Metrics</h4>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {Object.entries(result.metrics).map(([key, value]) => (
                                      <div key={key} className="bg-muted/50 p-2 rounded">
                                        <span className="text-xs text-muted-foreground">{key}</span>
                                        <div className="text-sm font-medium">{String(value)}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
